import { useEffect, useState, forwardRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { useMap } from 'react-leaflet/hooks'
import { MAP_DEFAULT_ZOOM, MAP_SWEDEN_CENTER, MAP_USER_ZOOM } from '../../constants';
import LocationSearchingIcon from "@mui/icons-material/LocationSearching";
import DirectionsIcon from '@mui/icons-material/Directions';
import HomeIcon from '@mui/icons-material/Home';
import PhoneIcon from '@mui/icons-material/Phone';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import LanguageIcon from '@mui/icons-material/Language';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { DivIcon, Point } from 'leaflet';
import axios from 'axios';
import { Box, Button, Container, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material';
import { isIOS, isMobile } from 'react-device-detect';
import { IconPinBakery, IconPinCafe, IconPinCraft, IconPinFleaMarket, IconPinGarden, IconPinShopClothes, IconPinShopFood, IconPinShopInteriorDesign, MarkerIconBakery, MarkerIconFleaMarket, MarkerIconMulti, MarkerIconPinCafe, MarkerIconPinCraft, MarkerIconPinGarden, MarkerIconPinShopClothes, MarkerIconPinShopFood, MarkerIconPinShopInteriorDesign } from '../../icons';
import Slide from '@mui/material/Slide';
import CloseIcon from '@mui/icons-material/Close';
import { track } from '@vercel/analytics';

function UserLocation({ userLocation, setUserLocation }) {
  const map = useMap();

  // TODO: keep user location in global state
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation([position.coords.latitude, position.coords.longitude]);
        },
        (error) => {
        },
        {
          enableHighAccuracy: true
        }
      );
    }
  }, [setUserLocation]);

  useEffect(() => {
    if (userLocation === null) {
      return;
    }
    map.flyTo(userLocation, MAP_USER_ZOOM);
  }, [userLocation, map]);

  const icon = new DivIcon({
    html: '<div class="back"><div class="front"></div></div>',
    className: "marker my-location"
  });

  return userLocation === null ? null : <Marker position={userLocation} icon={icon} />;
}

function FindMe({ userLocation }) {
  const map = useMap();

  const onClick = () => {
    map.flyTo(userLocation);
  }

  return (
    <IconButton
      onClick={onClick}
      sx={{
        position: "absolute",
        right: "12px",
        bottom: "59px",
        zIndex: "1200",
        backgroundColor: "#ffffff",
        filter: "drop-shadow(1px 1px 2px rgb(0 0 0 / 0.2))",
        "&:hover": {
          backgroundColor: "#ffffff"
        }
      }}>
      <LocationSearchingIcon color="primary" />
    </IconButton>
  )
}

function getIconByTag(tag, key) {
  switch (tag) {
    case "cafe":
      return <IconPinCafe key={key} />;
    case "shopFood":
      return <IconPinShopFood key={key} />;
    case "shopInteriorDesign":
      return <IconPinShopInteriorDesign key={key} />;
    case "accommodations":
      return <IconPinShopClothes key={key} />;
    case "garden":
      return <IconPinGarden key={key} />;
    case "craft":
      return <IconPinCraft key={key} />;
    case "fleaMarket":
      return <IconPinFleaMarket key={key} />;
    case "bakery":
      return <IconPinBakery key={key} />;
    default:
      return <></>;
  }
}

function getMarkerIcon(item) {
  if (item.tags.length > 1) {
    return MarkerIconMulti;
  }

  switch (item.tags[0]) {
    case "cafe":
      return MarkerIconPinCafe;
    case "shopFood":
      return MarkerIconPinShopFood;
    case "shopInteriorDesign":
      return MarkerIconPinShopInteriorDesign;
    case "accommodations":
      return MarkerIconPinShopClothes;
    case "garden":
      return MarkerIconPinGarden;
    case "craft":
      return MarkerIconPinCraft;
    case "fleaMarket":
      return MarkerIconFleaMarket;
    case "bakery":
      return MarkerIconBakery;
    default:
      return;
  }
}

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function MapMarkers({ item }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const handleMobileClose = () => {
    setMobileOpen(false);
  };

  const createLink = (item) => {
    return isIOS
      ? `https://maps.apple.com/?ll=${item.lat},${item.lng}&q=${item.address}`
      : `https://www.google.com/maps/search/?api=1&query=${item.lat},${item.lng}`;
  }

  const phoneNumber = (item) => {
    if (!item.phone) {
      return <></>
    }

    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <PhoneIcon color="primary" />
        {isMobile
          ? <Typography
            variant="caption"
            component="a"
            href={`tel:${item.phone}`}
            target="_blank">
            {item.phone}
          </Typography>
          : <Typography variant="caption">{item.phone}</Typography>
        }
      </Stack >
    )
  }

  const instagram = (item) => {
    if (!item.instagram) {
      return;
    }

    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <InstagramIcon color="primary" />
        <Typography
          variant="caption"
          component="a"
          href={item.instagram}
          target="_blank">
          Instagram
        </Typography>
      </Stack >
    )
  }

  const facebook = (item) => {
    if (!item.facebook) {
      return;
    }

    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <FacebookIcon color="primary" />
        <Typography
          variant="caption"
          component="a"
          href={item.facebook}
          target="_blank">
          Facebook
        </Typography>
      </Stack >
    )
  }

  const web = (item) => {
    if (!item.website) {
      return;
    }

    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <LanguageIcon color="primary" />
        <Typography
          variant="caption"
          component="a"
          href={item.website}
          target="_blank">
          {item.website}
        </Typography>
      </Stack >
    )
  }

  const reportage = (item) => {
    if (!item.reportage) {
      return;
    }

    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <YouTubeIcon color="primary" />
        <Typography
          variant="caption"
          component="a"
          href={item.reportage}
          target="_blank">
          Reportage
        </Typography>
      </Stack >
    )
  }

  const logo = (item) => {
    if (!item.logo) {
      return;
    }

    return (
      <Box
        component="img"
        sx={{
          maxHeight: 100,
          objectFit: "contain",
          borderRadius: "20px",
          filter: "drop-shadow(1px 1px 2px rgb(0 0 0 / 0.2))"
        }}
        alt={item.name}
        src={`/logos/${item.logo}`}
      />
    )
  }

  const content = (item) => {
    return (
      <Container
        disableGutters
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center"
        }}>
        {logo(item)}
        <Typography variant="h6">{item.name}</Typography>
        <Stack alignItems="center" direction="row" className="popup-icon-list" >
          {item.tags.map((tag, tagIndex) => (
            getIconByTag(tag, tagIndex)
          ))}
        </Stack>
        <Stack spacing={2} useFlexGap sx={{ paddingTop: 2 }}>
          <Typography
            variant="body2"
            sx={{ textAlign: "center", textWrap: "wrap", width: "320px", whiteSpace: "pre-line" }}>
            {item.description}
          </Typography>

          <Stack spacing={1} className="popup-contact">
            <Stack direction="row" alignItems="center" gap={1}>
              <HomeIcon color="primary" />
              <Typography variant="caption">{item.address}</Typography>
            </Stack>
            {phoneNumber(item)}
            {instagram(item)}
            {facebook(item)}
            {web(item)}
            {reportage(item)}
          </Stack>

          <Button
            variant="contained"
            sx={{ color: "#ffffff !important" }}
            startIcon={<DirectionsIcon />}
            component="a"
            href={createLink(item)}
            target="_blank"
            onClick={() => {
              track("DirectionsClicked", {
                name: item.name,
                latlng: `${item.lat}, ${item.lng}`
              });
            }}>
            Vägbeskrivning
          </Button>
        </Stack>
      </Container>
    )
  }

  return (
    <Marker position={[item.lat, item.lng]} icon={getMarkerIcon(item)} eventHandlers={{
      click: (e) => {
        if (isMobile) {
          setMobileOpen(true);
        }

        track("OpenPin", {
          name: item.name,
          latlng: `${item.lat}, ${item.lng}`
        });
      }
    }}>
      {isMobile
        ? <Dialog
          fullScreen
          open={mobileOpen}
          onClose={handleMobileClose}
          TransitionComponent={Transition}
        >
          <IconButton component="a" sx={{ ml: "auto", right: 0 }} onClick={handleMobileClose}>
            <CloseIcon />
          </IconButton>
          <DialogContent sx={{
            padding: "0"
          }}>
            {content(item)}
          </DialogContent>
        </Dialog> :
        <Popup maxWidth={320} minWidth={320} autoPanPaddingTopLeft={new Point(0, 120)}>
          {content(item)}
        </Popup>
      }
    </Marker>
  );
}

function Map({ filter }) {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    axios.get("/data.json").then((res) => {
      setData(res.data);
      setFilteredData(res.data);
    });
  }, []);

  useEffect(() => {
    if (filter === null) {
      return;
    }

    const activeFilter = [];
    for (const item in filter) {
      if (filter.hasOwnProperty(item) && filter[item] === true) {
        activeFilter.push(item);
      }
    }

    const items = data.filter((item) => item.tags.some(tag => activeFilter.includes(tag)));
    setFilteredData(items);
  }, [filter, data]);

  return (
    <MapContainer
      center={MAP_SWEDEN_CENTER}
      zoom={MAP_DEFAULT_ZOOM}
      scrollWheelZoom={true}
      zoomControl={false}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <UserLocation userLocation={userLocation} setUserLocation={setUserLocation} />
      {filteredData.map((item, index) => (
        <MapMarkers key={index} item={item} />
      ))}

      <FindMe userLocation={userLocation} />
    </MapContainer>
  );
}

export default Map;